import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import Landing from "@/pages/Landing";
import { Login, Signup, ForgotPassword, ResetPassword } from "@/pages/Auth";
import StaffPin from "@/pages/StaffPin";
import Dashboard from "@/pages/Dashboard";
import Checks from "@/pages/Checks";
import { useBranding } from "@/lib/branding";
import Templates from "@/pages/Templates";
import Temps from "@/pages/Temps";
import Incidents from "@/pages/Incidents";
import Deliveries from "@/pages/Deliveries";
import { Equipment, Training, Suppliers } from "@/pages/Registers";
import Allergens from "@/pages/Allergens";
import Haccp from "@/pages/Haccp";
import Assistant from "@/pages/Assistant";
import Team from "@/pages/Team";
import Settings from "@/pages/Settings";
import Billing from "@/pages/Billing";
import AuditPack from "@/pages/AuditPack";
import AuditLog from "@/pages/AuditLog";
import Records from "@/pages/Records";
import Inspect from "@/pages/Inspect";
import Demo from "@/pages/Demo";
import Tour from "@/pages/Tour";
import Prep from "@/pages/Prep";
import SelfInspections from "@/pages/SelfInspections";
import Insights from "@/pages/Insights";
import Cooling from "@/pages/Cooling";
import Contractors from "@/pages/Contractors";
import PublicMenu from "@/pages/PublicMenu";
import Platform from "@/pages/Platform";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";

const Loading = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
  </div>
);

function Protected({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return children;
}

function Home() {
  const { user } = useAuth();
  return user.business_id ? <Dashboard /> : <Navigate to="/app/platform" replace />;
}

function GuestOnly({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loading />;
  const next = new URLSearchParams(loc.search).get("next");
  if (user) return <Navigate to={next && next.startsWith("/app") ? next : "/app"} replace />;
  return children;
}

function Gate({ perm, children }) {
  const { can } = useAuth();
  return can(perm) ? children : <Navigate to="/app" replace />;
}

export default function App() {
  useBranding();
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
            <Route path="/signup" element={<GuestOnly><Signup /></GuestOnly>} />
            <Route path="/forgot-password" element={<GuestOnly><ForgotPassword /></GuestOnly>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/inspect/:token" element={<Inspect />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/tour" element={<Tour />} />
            <Route path="/menu/:token" element={<PublicMenu />} />
            <Route path="/b/:slug" element={<StaffPin />} />
            <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
            <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
            <Route path="/app" element={<Protected><AppShell /></Protected>}>
              <Route index element={<Home />} />
              <Route path="checks" element={<Checks />} />
              <Route path="temps" element={<Temps />} />
              <Route path="incidents" element={<Incidents />} />
              <Route path="deliveries" element={<Deliveries />} />
              <Route path="prep" element={<Prep />} />
              <Route path="cooling" element={<Cooling />} />
              <Route path="contractors" element={<Contractors />} />
              <Route path="self-inspections" element={<SelfInspections />} />
              <Route path="insights" element={<Gate perm="admin"><Insights /></Gate>} />
              <Route path="equipment" element={<Equipment />} />
              <Route path="allergens" element={<Allergens />} />
              <Route path="haccp" element={<Haccp />} />
              <Route path="training" element={<Training />} />
              <Route path="suppliers" element={<Suppliers />} />
              <Route path="assistant" element={<Assistant />} />
              <Route path="team" element={<Gate perm="admin"><Team /></Gate>} />
              <Route path="templates" element={<Gate perm="manage"><Templates /></Gate>} />
              <Route path="audit-pack" element={<Gate perm="audit"><AuditPack /></Gate>} />
              <Route path="audit-log" element={<Gate perm="audit"><AuditLog /></Gate>} />
              <Route path="records" element={<Gate perm="admin"><Records /></Gate>} />
              <Route path="settings" element={<Gate perm="admin"><Settings /></Gate>} />
              <Route path="billing" element={<Gate perm="billing"><Billing /></Gate>} />
              <Route path="platform" element={<Platform />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}
